// studentName comes back only for a student's own login code (2026-10-03).
type ExchangeResult = { sessionStatus: string; studentName?: string };

/**
 * A failed sign-in. `message` is the server's error code (as before);
 * `studentName` is set only when the code was right but no work is
 * waiting, so the page can still greet the child by name.
 */
export class LearnerExchangeError extends Error {
  readonly studentName: string | null;
  constructor(code: string, studentName: string | null) {
    super(code);
    this.name = 'LearnerExchangeError';
    this.studentName = studentName;
  }
}
type ResumeResult = { sessionId: string; sessionStatus: string };
// `practice` is the next question, sent back with a submitted answer since
// 2026-10-03 so the screen can move on without a second trip. It can be
// missing (older server, or the read failed): then fetch it as before.
export type ActionResult = {
  sessionStatus: string;
  itemsCompleted: number;
  practice?: CurrentPractice;
};
export type CurrentPractice = {
  sessionStatus: string;
  purpose: string;
  itemsCompleted: number;
  itemsTotal: number;
  stoppingRuleType: 'item_count' | 'time_limit';
  // The teacher's time limit, if any, and active time used so far (paused
  // time not counted). Absent from older responses.
  timeLimitSeconds?: number | null;
  secondsUsed?: number | null;
  currentItem: {
    id: string;
    content: unknown;
    responseForm: string;
    accessibility: {
      plainLanguageSummary: string;
      keyboardOperable: boolean;
      transcriptAvailable: boolean;
    };
    requiresResponse: boolean;
  } | null;
  contentEligibilityException: 'CONTENT_INELIGIBLE' | null;
};

async function parseErrorOrThrow(response: Response): Promise<never> {
  const body = (await response
    .json()
    .catch(() => ({ error: 'REQUEST_FAILED' }))) as {
    error?: string;
  };
  throw new Error(body.error ?? 'REQUEST_FAILED');
}

export function createLearnerSessionClient() {
  return {
    async exchangeEntryCode(code: string): Promise<ExchangeResult> {
      const response = await fetch('/api/learner/exchange', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      if (!response.ok) {
        const body = (await response
          .json()
          .catch(() => ({ error: 'REQUEST_FAILED' }))) as {
          error?: string;
          studentName?: unknown;
        };
        throw new LearnerExchangeError(
          body.error ?? 'REQUEST_FAILED',
          typeof body.studentName === 'string' ? body.studentName : null,
        );
      }
      return (await response.json()) as ExchangeResult;
    },
    // "Ask for work" on the no-work welcome screen. Says who was asked (the
    // adult whose code it is); this also covers a child who has already
    // asked. 'work_waiting' means work turned up in the meantime, so the
    // caller should sign them in instead.
    async askForWork(
      code: string,
    ): Promise<'teacher' | 'grown_up' | 'work_waiting'> {
      const response = await fetch('/api/learner/exchange', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ code, askForWork: true }),
      });
      if (response.ok) {
        const asked = (await response.json().catch(() => ({}))) as {
          askedOf?: unknown;
        };
        return asked.askedOf === 'grown_up' ? 'grown_up' : 'teacher';
      }
      const body = (await response
        .json()
        .catch(() => ({ error: 'REQUEST_FAILED' }))) as { error?: string };
      if (body.error === 'WORK_WAITING') return 'work_waiting';
      throw new Error(body.error ?? 'REQUEST_FAILED');
    },
    async resume(): Promise<ResumeResult> {
      const response = await fetch('/api/learner/resume', {
        method: 'GET',
        credentials: 'same-origin',
      });
      if (!response.ok) return parseErrorOrThrow(response);
      return (await response.json()) as ResumeResult;
    },
    async getCurrentPractice(): Promise<CurrentPractice> {
      const response = await fetch('/api/learner/practice', {
        method: 'GET',
        credentials: 'same-origin',
      });
      if (!response.ok) return parseErrorOrThrow(response);
      return (await response.json()) as CurrentPractice;
    },
    async exit(): Promise<void> {
      // DM-IMP-005 changes-required remediation, Task 4: previously the
      // response was never inspected at all -- a failed/errored exit call
      // resolved exactly like a successful one. Now this follows the same
      // check-response.ok-and-throw convention as every other method here,
      // so the caller (SafeExit.tsx) can genuinely distinguish confirmed
      // success from a failure it must render honestly.
      const response = await fetch('/api/learner/exit', {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!response.ok) return parseErrorOrThrow(response);
    },
    async sendAction(
      action: string,
      payload: unknown,
      idempotencyKey: string,
    ): Promise<ActionResult> {
      const response = await fetch('/api/learner/action', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ action, payload, idempotencyKey }),
      });
      if (!response.ok) return parseErrorOrThrow(response);
      return (await response.json()) as ActionResult;
    },
  };
}
